import { useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { useLocation, useRouter } from '@tanstack/react-router';
import { useStore } from '@nanostores/react';
import type { TabNavTabItem } from '@customerio/pluma-components/react';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { billing } from '@/services/billing';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { API_TRIGGERED_CAMPAIGN_TYPE, useApiTriggeredBroadcasts } from '@/services/db/campaign/queries';
import { useFilteredNewsletters } from '@/services/db/newsletter/queries';
import {
  toApiTriggeredListParams,
  toNewsletterListParams,
  validateApiTriggeredSearch,
  validateNewslettersSearch,
  withoutDefaults,
  type ApiTriggeredSearch,
  type NewslettersSearch,
  type RawSearch,
} from './url-state';
import { BROADCASTS_TAB_LABELS, BROADCASTS_TAB_ROUTES, broadcastsTabFromPathname, type BroadcastsTab } from './tabs';

/** Each tab's search, as the reader last left it on that tab. */
export type SavedBroadcastsSearches = {
  one_time_sends?: NewslettersSearch;
  'api-triggered'?: ApiTriggeredSearch;
};

/** One tab's badge and welcome inputs. `null` means unknown: the read is pending or failed. */
type TabCounts = {
  /** Rows matching the tab's filters, which the badge and the pagination show. */
  total: number | null;
  /** Everything the workspace has on this tab, filters aside. */
  allTime: number | null;
  /** Whether the read has settled, successfully or not. */
  settled: boolean;
};

const NEWSLETTERS_ERROR = "Your one-time sends couldn't be loaded. Try again.";
const API_TRIGGERED_ERROR = "Your API-triggered broadcasts couldn't be loaded. Try again.";

/**
 * A tab link carries that tab's own filters as the reader left them, but the search box is
 * shared: it always carries the current search, and a search that changed since the tab was
 * last visited starts the tab over at the first page.
 */
function tabSearchForLink(
  saved: NewslettersSearch | ApiTriggeredSearch | undefined,
  search: string | undefined,
): NewslettersSearch | ApiTriggeredSearch {
  if (saved == null) {
    return { search };
  }
  if (saved.search === search) {
    return saved;
  }
  return { ...saved, search, page: undefined };
}

/** One-time sends' badge and welcome inputs, from its read. */
function newslettersCounts(query: ReturnType<typeof useFilteredNewsletters>): TabCounts {
  return {
    total: query.data?.pagination.total ?? null,
    allTime: query.data?.totals.all_time ?? null,
    settled: !query.isPending,
  };
}

/**
 * API-triggered broadcasts' badge and welcome inputs. A Builder account never reads this tab:
 * it counts as settled and empty, which lets the loader and the welcome state resolve without it.
 */
function apiTriggeredCounts(query: ReturnType<typeof useApiTriggeredBroadcasts>, isBuilder: boolean): TabCounts {
  if (isBuilder) {
    return { total: 0, allTime: 0, settled: true };
  }
  const meta = query.data?.meta;
  return {
    total: meta?.pagination.total ?? null,
    // All-time counts are keyed by campaign type, and a type with none is left out, so a
    // missing key on a response that did arrive is a zero rather than an unknown.
    allTime: query.data == null ? null : (meta?.totals.all_time[API_TRIGGERED_CAMPAIGN_TYPE] ?? 0),
    settled: !query.isPending,
  };
}

/**
 * Each tab's search as the reader last left it, for as long as the page stays open.
 *
 * Adjusted during render rather than in an effect: it derives from the URL this render is
 * for, and the tab links rendered from it have to see it on the same pass.
 */
function useSavedTabSearches(
  activeTab: BroadcastsTab | null,
  activeSearch: NewslettersSearch | ApiTriggeredSearch | undefined,
): SavedBroadcastsSearches {
  const [saved, setSaved] = useState<SavedBroadcastsSearches>({});
  if (activeTab == null || activeSearch == null) {
    return saved;
  }
  const current = withoutDefaults(activeSearch);
  const previous = saved[activeTab];
  if (previous == null || JSON.stringify(withoutDefaults(previous)) !== JSON.stringify(current)) {
    setSaved((all) => ({ ...all, [activeTab]: current }));
  }
  return saved;
}

/** The URL's search, validated for whichever tab it is on. */
function activeTabSearches(activeTab: BroadcastsTab | null, rawSearch: RawSearch) {
  return {
    newslettersSearch: activeTab === 'one_time_sends' ? validateNewslettersSearch(rawSearch) : undefined,
    apiTriggeredSearch: activeTab === 'api-triggered' ? validateApiTriggeredSearch(rawSearch) : undefined,
  };
}

/**
 * What each tab is read with: the showing tab with the URL's filters, the other with the
 * filters it was last left with, or none if the reader hasn't been there.
 */
function tabReadParams({
  newslettersSearch,
  apiTriggeredSearch,
  saved,
  showGoals,
}: {
  newslettersSearch: NewslettersSearch | undefined;
  apiTriggeredSearch: ApiTriggeredSearch | undefined;
  saved: SavedBroadcastsSearches;
  showGoals: boolean;
}) {
  return {
    newsletters: toNewsletterListParams(newslettersSearch ?? saved.one_time_sends ?? {}, { showGoals }),
    apiTriggered: toApiTriggeredListParams(apiTriggeredSearch ?? saved['api-triggered'] ?? {}, { showGoals }),
  };
}

/**
 * The frame's page-level states. It loads until both tabs have settled, succeeded or not, since
 * a failed read leaves its count unknown rather than blocking the page; and it welcomes the
 * reader only when both all-time counts are known to be zero.
 */
function frameSummary(counts: Record<BroadcastsTab, TabCounts>, activeTab: BroadcastsTab | null) {
  const tabs = Object.values(counts);
  return {
    isLoading: !tabs.every((tab) => tab.settled),
    shouldDisplayWelcome: tabs.every((tab) => tab.allTime === 0),
    /** Rows matching the showing tab's filters, for its pagination. */
    total: activeTab == null ? 0 : (counts[activeTab].total ?? 0),
  };
}

/** The tab bar's items: each tab's link, label and count, in the order the account shows them. */
function tabItems({
  tabOrder,
  activeTab,
  counts,
  hrefFor,
}: {
  tabOrder: readonly BroadcastsTab[];
  activeTab: BroadcastsTab | null;
  counts: Record<BroadcastsTab, TabCounts>;
  hrefFor: (tab: BroadcastsTab) => string;
}): TabNavTabItem[] {
  return tabOrder.map((tab) => ({
    href: hrefFor(tab),
    text: BROADCASTS_TAB_LABELS[tab],
    isActive: tab === activeTab,
    // An unknown count, a pending or failed read, shows as a dash rather than a zero.
    badge: counts[tab].total == null ? '-' : String(counts[tab].total),
  }));
}

/** The showing tab's read, and how many rows it has on the current page. */
function showingTabResults(
  activeTab: BroadcastsTab | null,
  newslettersQuery: ReturnType<typeof useFilteredNewsletters>,
  apiTriggeredQuery: ReturnType<typeof useApiTriggeredBroadcasts>,
) {
  if (activeTab === 'api-triggered') {
    return { activeQuery: apiTriggeredQuery, activeRowCount: apiTriggeredQuery.data?.campaigns.length ?? 0 };
  }
  return { activeQuery: newslettersQuery, activeRowCount: newslettersQuery.data?.newsletters.length ?? 0 };
}

/**
 * The state of the Broadcasts list frame: which tab is showing, what each tab link points
 * at, the counts its badges show, and whether the page is still loading or empty enough to
 * welcome the reader instead.
 *
 * Both tabs are read here, the showing one with its filters and the other with the filters
 * it was last left with (or none), so both badges have a count. The showing tab's body reads
 * the same query, so there is one request per tab, not two.
 */
export function useBroadcastsList() {
  const workspaceId = requireWorkspaceId();
  const router = useRouter();
  const { pathname, search: rawSearch } = useLocation();
  const isBuilder = useStore(billing.isBuilder);
  const showGoals = useFeatureFlag(AccountFeatureFlag.ShowGoals);

  const activeTab = broadcastsTabFromPathname(pathname);
  const { newslettersSearch, apiTriggeredSearch } = activeTabSearches(activeTab, rawSearch);
  const activeSearch = newslettersSearch ?? apiTriggeredSearch;
  const saved = useSavedTabSearches(activeTab, activeSearch);

  const readParams = tabReadParams({ newslettersSearch, apiTriggeredSearch, saved, showGoals });
  // A search, filter or page change keeps the last results up (and the frame mounted) until
  // the new ones land, so only the first load of the page shows a loader.
  const newslettersQuery = useFilteredNewsletters({
    params: readParams.newsletters,
    queryConfig: { meta: { errorMessage: NEWSLETTERS_ERROR }, placeholderData: keepPreviousData },
  });
  const apiTriggeredQuery = useApiTriggeredBroadcasts({
    params: readParams.apiTriggered,
    enabled: !isBuilder,
    queryConfig: { meta: { errorMessage: API_TRIGGERED_ERROR }, placeholderData: keepPreviousData },
  });

  const counts = {
    one_time_sends: newslettersCounts(newslettersQuery),
    'api-triggered': apiTriggeredCounts(apiTriggeredQuery, isBuilder),
  };
  const searchValue = activeSearch?.search;
  const tabs = tabItems({
    tabOrder: isBuilder ? ['one_time_sends'] : ['one_time_sends', 'api-triggered'],
    activeTab,
    counts,
    hrefFor: (tab) =>
      router.buildLocation({
        to: BROADCASTS_TAB_ROUTES[tab],
        params: { workspaceId: String(workspaceId) },
        search: withoutDefaults(tabSearchForLink(saved[tab], searchValue)),
      }).href,
  });
  const { activeQuery, activeRowCount } = showingTabResults(activeTab, newslettersQuery, apiTriggeredQuery);

  return {
    workspaceId,
    activeTab,
    activeSearch,
    saved,
    tabs,
    isBuilder,
    ...frameSummary(counts, activeTab),
    /** Whether the showing tab has rows on this page, which is when the bottom pagination shows. */
    hasRows: activeRowCount > 0,
    page: activeSearch?.page ?? 1,
    search: searchValue ?? '',
    activeQuery,
    newslettersQuery,
    apiTriggeredQuery,
  };
}

export type BroadcastsListState = ReturnType<typeof useBroadcastsList>;
